"use client";

import { publicStorePath } from "@/lib/storefront/public-url";
import { useState } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Mail, Lock, Eye, EyeOff, User, CheckCircle2 } from "lucide-react";
import { getDefaultChannelSlug } from "@/lib/storefront/channel";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { cn } from "@/lib/utils";

import { localizedAuthError } from "@/lib/locale/auth-error";

interface SignUpFormProps {
	containerClassName?: string;
}

export function SignUpForm({ containerClassName }: SignUpFormProps) {
	const params = useParams<{ locale: string }>();
	const channel = getDefaultChannelSlug();
	const t = useTranslations("signup");
	const tAuth = useTranslations("auth");
	const resolvedContainerClassName = cn("mx-auto w-full max-w-md", containerClassName ?? "mt-16");
	const surfaceClassName = "border border-border bg-card p-6 shadow-sm sm:p-8";
	const destructiveNoticeClassName =
		"rounded-xl border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive";
	const helperTextClassName = "text-center text-xs leading-5 text-muted-foreground";

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);

	const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		// Validation
		if (!email || !validateEmail(email)) {
			setError(tAuth("invalidEmail"));
			return;
		}

		if (password.length < 8) {
			setError(tAuth("passwordMinLength"));
			return;
		}

		if (password !== confirmPassword) {
			setError(tAuth("passwordsNoMatch"));
			return;
		}

		setIsSubmitting(true);

		try {
			// Call Saleor accountRegister mutation
			const response = await fetch("/api/auth/register", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email,
					password,
					firstName,
					lastName,
					channel: channel,
					locale: params.locale,
					redirectUrl: `${window.location.origin}${publicStorePath(params.locale, "/account/confirm")}`,
				}),
			});

			const data = (await response.json()) as {
				errors?: Array<{ message: string; code?: string; field?: string | null }>;
				user?: { id: string; email: string };
			};

			if (data.errors?.length) {
				const err = data.errors[0];
				if (err.code === "UNIQUE") {
					setError(t("accountExists"));
				} else {
					setError(localizedAuthError(params.locale, err, "register", tAuth, t("failedToCreate")));
				}
				return;
			}

			// Success - show confirmation message
			setSuccess(true);
		} catch {
			setError(tAuth("genericError"));
		} finally {
			setIsSubmitting(false);
		}
	};

	if (success) {
		return (
			<div className={resolvedContainerClassName}>
				<div className={surfaceClassName}>
					<div className="text-center">
						<div className="bg-success/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
							<CheckCircle2 className="h-6 w-6 text-success" />
						</div>
						<h2 className="text-3xl font-semibold tracking-tight text-foreground">{t("accountCreated")}</h2>
						<p className="mt-3 text-sm leading-6 text-muted-foreground sm:text-base">{t("checkEmail")}</p>
						<Link
							href={publicStorePath(
								params.locale,
								`/login?next=${encodeURIComponent(publicStorePath(params.locale, "/account?welcome=1"))}`,
							)}
							className="mt-6 inline-block text-sm font-medium text-foreground underline underline-offset-2 hover:no-underline"
						>
							{t("goToSignIn")}
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={resolvedContainerClassName}>
			<div className={surfaceClassName}>
				<div className="mb-8 text-center">
					<h1 className="text-3xl font-semibold tracking-tight text-foreground">{t("createAccount")}</h1>
					<p className="mt-3 text-sm leading-6 text-muted-foreground">
						{t("alreadyHaveAccount")}{" "}
						<Link
							href={`${publicStorePath(params.locale, "/login")}`}
							className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
						>
							{tAuth("signIn")}
						</Link>
					</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-5">
					{error && <div className={destructiveNoticeClassName}>{error}</div>}

					{/* Name fields */}
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="firstName" className="text-sm font-medium">
								{t("firstName")}
							</Label>
							<div className="relative">
								<User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id="firstName"
									type="text"
									placeholder={t("firstNamePlaceholder")}
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									className="h-12 pl-10"
								/>
							</div>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="lastName" className="text-sm font-medium">
								{t("lastName")}
							</Label>
							<div className="relative">
								<User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id="lastName"
									type="text"
									placeholder={t("lastNamePlaceholder")}
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									className="h-12 pl-10"
								/>
							</div>
						</div>
					</div>

					{/* Email */}
					<div className="space-y-1.5">
						<Label htmlFor="email" className="text-sm font-medium">
							{tAuth("email")}
						</Label>
						<div className="relative">
							<Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								id="email"
								type="email"
								placeholder={tAuth("emailPlaceholder")}
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="h-12 pl-10"
								required
							/>
						</div>
					</div>

					{/* Password */}
					<div className="space-y-1.5">
						<Label htmlFor="password" className="text-sm font-medium">
							{tAuth("password")}
						</Label>
						<div className="relative">
							<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								id="password"
								type={showPassword ? "text" : "password"}
								placeholder={t("minimumChars")}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="h-12 pl-10 pr-10"
								required
								minLength={8}
							/>
							<button
								type="button"
								onClick={() => setShowPassword(!showPassword)}
								className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
							>
								{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
							</button>
						</div>
					</div>

					{/* Confirm Password */}
					<div className="space-y-1.5">
						<Label htmlFor="confirmPassword" className="text-sm font-medium">
							{tAuth("confirmPassword")}
						</Label>
						<div className="relative">
							<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								id="confirmPassword"
								type={showPassword ? "text" : "password"}
								placeholder={t("reenterPassword")}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								className={cn(
									"h-12 pl-10",
									confirmPassword && password !== confirmPassword && "border-destructive",
								)}
								required
							/>
						</div>
						{confirmPassword && password !== confirmPassword && (
							<p className="text-sm text-destructive">{tAuth("passwordsNoMatch")}</p>
						)}
					</div>

					<Button type="submit" disabled={isSubmitting} className="h-12 w-full text-base font-semibold">
						{isSubmitting ? t("creatingAccount") : t("createAccountButton")}
					</Button>

					<p className={helperTextClassName}>{tAuth("formTrustNote")}</p>

					<p className="text-center text-xs leading-5 text-muted-foreground">
						{t("termsAgreement")}{" "}
						<Link
							href={`${publicStorePath(params.locale, "/terms")}`}
							className="underline hover:no-underline"
							target="_blank"
							rel="noopener noreferrer"
						>
							{t("termsOfService")}
						</Link>{" "}
						{t("andText")}{" "}
						<Link
							href={`${publicStorePath(params.locale, "/privacy")}`}
							className="underline hover:no-underline"
							target="_blank"
							rel="noopener noreferrer"
						>
							{t("privacyPolicy")}
						</Link>
					</p>
				</form>
			</div>
		</div>
	);
}
